// Examples explain real capabilities; they do not define separate execution workflows.
export const productLifecycle = [
  {
    "id": "shape",
    "label": "Shape",
    "contribution": "Give agents product and design direction.",
    "question": "What are you trying to change?",
    "title": "Find out what’s worth building.",
    "body": "Bring an idea or a product you already have. Find who needs it, what they use today and what to test before you invest more time.",
    "prompt": "Use Pabs to research alternatives to my tutoring app. Who is underserved, and what could we test before building?",
    "position": [
      50,
      9
    ],
    "actions": [
      {
        "lead": "Start from your existing project",
        "capability": {
          "name": "pabs-init",
          "doc": "install-pabs",
          "kind": "skill"
        },
        "outcome": "Use the notes and code you already have to establish your Brief and Product Strategy."
      },
      {
        "lead": "Compare the alternatives",
        "capability": {
          "name": "Competitor Analysis",
          "doc": "competitor-analysis",
          "kind": "feature"
        },
        "outcome": "See who competitors serve and where your idea could offer something useful."
      },
      {
        "lead": "Test the idea before committing",
        "capability": {
          "name": "ask_pabs_agent",
          "doc": "commands-shape",
          "kind": "tool"
        },
        "outcome": "Ask what needs to be true and which small test would help you decide."
      }
    ]
  },
  {
    "id": "decide",
    "label": "Decide",
    "contribution": "Bring key decisions back to you.",
    "question": "What is worth doing next?",
    "title": "Keep your decisions connected to what gets built.",
    "body": "When AI proposes a change, check whether it serves your goal. Keep your call, the resulting work and the evidence connected, so you can see what happened and what needs reconsidering.",
    "prompt": "Use Pabs to record our decision to shorten signup. Link the build task to this decision and define what evidence would show it helped.",
    "position": [
      86,
      35
    ],
    "actions": [
      {
        "lead": "Check the change against your goal",
        "capability": {
          "name": "ask_pabs_agent",
          "doc": "commands-decide",
          "kind": "tool"
        },
        "outcome": "Weigh a shorter signup against your product goal, design principles and what people need before changing the flow."
      },
      {
        "lead": "Carry your choice through to the build",
        "capability": {
          "name": "decide_build_decision",
          "doc": "commands-decide",
          "kind": "tool"
        },
        "outcome": "Your approval creates linked work with an expected result. Returned evidence stays tied to the decision, so you can check what was built and why."
      },
      {
        "lead": "Find what still needs your call",
        "capability": {
          "name": "get_pending_decisions",
          "doc": "commands-decide",
          "kind": "tool"
        },
        "outcome": "Pick up unresolved choices before the next session makes assumptions."
      }
    ]
  },
  {
    "id": "build",
    "label": "Build",
    "contribution": "Give agents the agreed scope.",
    "question": "What does the team need to get started?",
    "title": "Stop spending tokens on the wrong work.",
    "body": "A fast build can still solve the wrong problem. Once you agree the change, give your builder the product goal, design rules and scope it needs to deliver it without unnecessary rework.",
    "prompt": "Use Pabs to prepare a design brief for shorter signup. Follow our approved components and brand rules, cover errors and recovery, and keep the change within the agreed scope.",
    "position": [
      73,
      83
    ],
    "actions": [
      {
        "lead": "Keep a small fix from becoming a redesign",
        "capability": {
          "name": "prepare_design_brief",
          "doc": "design-authority-start",
          "kind": "tool"
        },
        "outcome": "Have Design Authority turn the agreed signup change and your connected design sources into a brief: what to change, what to preserve and which states to cover."
      },
      {
        "lead": "Stop a new screen inventing its own components",
        "capability": {
          "name": "review_design_system_usage",
          "doc": "design-authority-check",
          "kind": "tool"
        },
        "outcome": "Check the builder’s forms, buttons and patterns against your design system while the work is taking shape."
      },
      {
        "lead": "Bring the result back to the decision",
        "capability": {
          "name": "complete_product_requirement",
          "doc": "jobs-and-routines",
          "kind": "tool"
        },
        "outcome": "Return the changes, checks and remaining gaps for the linked requirement, so the work stays connected to what you agreed."
      },
      {
        "lead": "Align people working in parallel",
        "capability": {
          "name": "Shared views",
          "doc": "sharing",
          "kind": "feature"
        },
        "outcome": "Your designer, developer and client can check the same brief and agreed scope through a read-only link. Recipients do not need a Pabs account."
      }
    ]
  },
  {
    "id": "check",
    "label": "Check",
    "contribution": "Review the work agents return.",
    "question": "What needs attention before you move on?",
    "title": "Check the work before release.",
    "body": "Choose checks that fit what changed: a new flow, a screen that differs from the design, or a form that is hard to use. Bring the build and test results.",
    "prompt": "Use Pabs to review this setup build against our approved design. Here are the screenshots and keyboard test results. What needs fixing or more evidence?",
    "position": [
      27,
      83
    ],
    "actions": [
      {
        "lead": "Find where people get stuck",
        "capability": {
          "name": "audit_user_journey",
          "doc": "design-authority-check",
          "kind": "tool"
        },
        "outcome": "Review the steps, wording and recovery paths for a task such as creating the first project."
      },
      {
        "lead": "Compare the build with the design",
        "capability": {
          "name": "compare_design_to_implementation",
          "doc": "design-authority-check",
          "kind": "tool"
        },
        "outcome": "See which behaviors and states differ from the approved version."
      },
      {
        "lead": "Check the accessibility evidence",
        "capability": {
          "name": "assess_accessibility_evidence",
          "doc": "design-authority-check",
          "kind": "tool"
        },
        "outcome": "Review keyboard tests, recordings and reports to separate demonstrated problems from missing checks."
      },
      {
        "lead": "Fix failed signup without redesigning the page",
        "capability": {
          "name": "generate_design_remediation_plan",
          "doc": "design-authority-improve",
          "kind": "tool"
        },
        "outcome": "Turn selected findings into a focused correction brief. Preserve unaffected behavior and return the corrected version for another review."
      }
    ]
  },
  {
    "id": "learn",
    "label": "Learn",
    "contribution": "Feed evidence into the next decision.",
    "question": "What should change based on what you learned?",
    "title": "Use what happened to choose what’s next.",
    "body": "Shipping is not the end of the decision. Pabs evaluates observations from your conversations, connected AI tools and scheduled Jobs against the current strategy, so new evidence can change what you build next.",
    "prompt": "Use Pabs to record this test result: people finished setup, but two could not find their new project. What should we reconsider?",
    "position": [
      14,
      35
    ],
    "actions": [
      {
        "lead": "Bring a customer problem back to the decision",
        "capability": {
          "name": "report_learning",
          "doc": "learnings-capturing",
          "kind": "tool"
        },
        "outcome": "People finish signup but cannot find their project. Send the test evidence from your AI tool so Pabs can assess what it means for the work."
      },
      {
        "lead": "Check whether the fix actually helped",
        "capability": {
          "name": "Jobs & Routines",
          "doc": "jobs-and-routines",
          "kind": "feature"
        },
        "outcome": "Schedule a weekly routine to review new onboarding feedback from your connected sources. Pabs runs the check and returns findings, even when you are busy building."
      }
    ]
  }
];
